/**
 * Unified User Profile Store — Sprint 17g-1
 *
 * CRUD + convenience methods for the 7-layer user profile.
 * Uses LangGraph Store via the shared MemoryStore interface.
 */
import { NS } from '@ownyou/shared-types';
import { recordIkigaiRouteLocalOutcome, } from '../engine/route-local-outcome';
import { buildProfileSummaryPrompt } from '../engine/profile-prompts';
export class UserProfileStore {
    store;
    userId;
    constructor(store, userId) {
        this.store = store;
        this.userId = userId;
    }
    ns() {
        return NS.userProfile(this.userId);
    }
    // --- Layer CRUD ---
    async getLayer(key) {
        return this.store.get(this.ns(), key);
    }
    async putLayer(key, data) {
        await this.store.put(this.ns(), key, data);
    }
    async loadFullProfile() {
        const [summary, identity, personality, interests, behaviors, context, advertising] = await Promise.all([
            this.getLayer('summary'),
            this.getLayer('identity'),
            this.getLayer('personality'),
            this.getLayer('interests'),
            this.getLayer('behaviors'),
            this.getLayer('context'),
            this.getLayer('advertising'),
        ]);
        return { summary, identity, personality, interests, behaviors, context, advertising };
    }
    // --- Convenience methods ---
    async addPreference(directive) {
        let summary = await this.getLayer('summary');
        if (!summary) {
            // Cold-start: create minimal summary so we don't lose the directive
            summary = {
                version: 1,
                generatedAt: 0,
                stage: 'cold_start',
                narrative: '',
                topInterests: [],
                activeContexts: [],
                directives: [],
                ikigaiWeights: { passion: 0, mission: 0, profession: 0, vocation: 0 },
                confidence: 0,
                dataPointCount: 0,
                dataSourceCount: 0,
            };
        }
        const directives = summary.directives.filter(d => !(d.condition === directive.condition && d.preference === directive.preference));
        directives.push(directive);
        summary.directives = directives.slice(-10);
        await this.putLayer('summary', summary);
    }
    async addAntiInterest(topic, evidence) {
        const interests = (await this.getLayer('interests')) ?? {
            updatedAt: Date.now(),
            clusters: [],
            antiInterests: [],
            brandAffinities: [],
        };
        if (interests.antiInterests.some(a => a.topic.toLowerCase() === topic.toLowerCase()))
            return;
        interests.antiInterests.push({ topic, strength: 0.8, evidence, since: Date.now() });
        // Cap at 20 anti-interests — evict oldest when exceeded
        if (interests.antiInterests.length > 20) {
            interests.antiInterests = interests.antiInterests
                .sort((a, b) => b.since - a.since)
                .slice(0, 20);
        }
        interests.updatedAt = Date.now();
        await this.putLayer('interests', interests);
    }
    async mergeInterest(topic, confidence) {
        const interests = (await this.getLayer('interests')) ?? {
            updatedAt: Date.now(),
            clusters: [],
            antiInterests: [],
            brandAffinities: [],
        };
        for (const cluster of interests.clusters) {
            const existing = cluster.topics.find(t => t.name.toLowerCase() === topic.toLowerCase());
            if (existing) {
                existing.strength = Math.max(existing.strength, confidence);
                cluster.lastActive = Date.now();
                cluster.accessCount++;
                interests.updatedAt = Date.now();
                await this.putLayer('interests', interests);
                return;
            }
        }
        if (interests.clusters.length < 12) {
            interests.clusters.push({
                id: crypto.randomUUID(),
                label: topic,
                topics: [{ name: topic, strength: confidence, engagementDepth: 'casual' }],
                lifecycle: 'discovery',
                trend: 'rising',
                firstSeen: Date.now(),
                lastActive: Date.now(),
                accessCount: 1,
                iabCategoryIds: [],
                confidence,
                dataPointCount: 1,
            });
        }
        interests.updatedAt = Date.now();
        await this.putLayer('interests', interests);
    }
    async recordEngagementSignal(category, action) {
        const behaviors = (await this.getLayer('behaviors')) ?? createEmptyBehaviors();
        const ACTION_TO_TOTAL = {
            presented: 'totalPresented',
            accepted: 'totalAccepted',
            dismissed: 'totalDismissed',
            completed: 'totalCompleted',
        };
        const key = ACTION_TO_TOTAL[action];
        behaviors.missionPatterns[key] = (behaviors.missionPatterns[key] ?? 0) + 1;
        if (!behaviors.missionPatterns.categoryRates[category]) {
            behaviors.missionPatterns.categoryRates[category] = {
                presented: 0,
                accepted: 0,
                dismissed: 0,
                completed: 0,
            };
        }
        behaviors.missionPatterns.categoryRates[category][action]++;
        behaviors.updatedAt = Date.now();
        await this.putLayer('behaviors', behaviors);
    }
    // --- LLM serialization ---
    async toPromptContext() {
        const summary = await this.getLayer('summary');
        if (!summary)
            return 'No user profile available yet.';
        const parts = [];
        if (summary.narrative) {
            parts.push(`USER PROFILE:\n${summary.narrative}`);
        }
        if (summary.topInterests.length > 0) {
            const interestLines = summary.topInterests
                .map(i => `- ${i.topic} (${i.trend}, strength ${(i.strength * 100).toFixed(0)}%)`)
                .join('\n');
            parts.push(`\nTOP INTERESTS:\n${interestLines}`);
        }
        if (summary.directives.length > 0) {
            const directiveLines = summary.directives
                .map(d => `- ${d.condition}: ${d.preference}`)
                .join('\n');
            parts.push(`\nPREFERENCES:\n${directiveLines}`);
        }
        const interests = await this.getLayer('interests');
        if (interests?.antiInterests && interests.antiInterests.length > 0) {
            const antiLines = interests.antiInterests.map(a => `- ${a.topic}`).join('\n');
            parts.push(`\nDO NOT SUGGEST (user dislikes):\n${antiLines}`);
        }
        if (summary.activeContexts.length > 0) {
            const contextLines = summary.activeContexts
                .filter(c => !c.expiresAt || c.expiresAt > Date.now())
                .map(c => `- ${c.context}`)
                .join('\n');
            if (contextLines) {
                parts.push(`\nCURRENT CONTEXTS:\n${contextLines}`);
            }
        }
        return parts.join('\n');
    }
    async toDetailedContext() {
        const base = await this.toPromptContext();
        const interests = await this.getLayer('interests');
        const behaviors = await this.getLayer('behaviors');
        const parts = [base];
        if (interests?.clusters && interests.clusters.length > 0) {
            const clusterLines = interests.clusters
                .map(c => `- ${c.label} [${c.lifecycle}/${c.trend}]: ${c.topics.map(t => t.name).join(', ')}`)
                .join('\n');
            parts.push(`\nINTEREST CLUSTERS:\n${clusterLines}`);
        }
        if (behaviors) {
            parts.push(`\nBEHAVIORAL SUMMARY:`);
            parts.push(`Missions: ${behaviors.missionPatterns.totalPresented} shown, ${behaviors.missionPatterns.totalAccepted} accepted, ${behaviors.missionPatterns.totalDismissed} dismissed`);
            if (behaviors.missionPatterns.acceptancePatterns.length > 0) {
                parts.push(`Accepts: ${behaviors.missionPatterns.acceptancePatterns.join('; ')}`);
            }
            if (behaviors.missionPatterns.dismissalPatterns.length > 0) {
                parts.push(`Avoids: ${behaviors.missionPatterns.dismissalPatterns.join('; ')}`);
            }
        }
        return parts.join('\n');
    }
    // --- Profile Summary Regeneration ---
    /**
     * Regenerate the profile summary from all layers.
     * Staleness guard: skips if existing summary is < 1 hour old (unless force=true).
     */
    async regenerateSummary(llm, force, modelPrefs) {
        let routeLocalOutcome;
        try {
            const model = modelPrefs?.profileSummary ?? modelPrefs?.fast;
            if (!model) {
                console.warn('[UserProfile] regenerateSummary called without profile summary model — skipping (no hardcoded model fallback)');
                routeLocalOutcome = {
                    operation: 'profileSummary',
                    status: 'not_run',
                    result: 'skipped',
                };
                return (await this.getLayer('summary')) ?? this.createEmptySummary();
            }
            if (!force) {
                const existingSummary = await this.getLayer('summary');
                if (existingSummary && Date.now() - existingSummary.generatedAt < 60 * 60 * 1000) {
                    return existingSummary;
                }
            }
            const identity = await this.getLayer('identity');
            const interests = await this.getLayer('interests');
            const behaviors = await this.getLayer('behaviors');
            const context = await this.getLayer('context');
            const personality = await this.getLayer('personality');
            // Sprint 17g-3: Personality inference when sufficient data
            const dataPoints = behaviors?.missionPatterns.totalPresented ?? 0;
            if (dataPoints >= 50) {
                const existingPersonality = personality; // Already loaded above
                const daysSinceUpdate = existingPersonality
                    ? (Date.now() - existingPersonality.updatedAt) / (1000 * 60 * 60 * 24)
                    : Infinity;
                if (daysSinceUpdate >= 30) {
                    try {
                        const { inferPersonality } = await import('../engine/personality-inferrer');
                        if (interests && behaviors) {
                            const newPersonality = await inferPersonality(interests, behaviors, identity, llm, model);
                            await this.putLayer('personality', newPersonality);
                        }
                    }
                    catch (err) {
                        console.warn('[UserProfile] Personality inference failed (non-blocking):', err);
                    }
                }
            }
            const prompt = buildProfileSummaryPrompt(identity, interests, behaviors, context, personality);
            let response;
            try {
                response = await llm.complete(prompt, {
                    model,
                    operation: 'profileSummary',
                    timeout: 10000,
                    maxTokens: 600,
                });
            }
            catch (err) {
                routeLocalOutcome = {
                    operation: 'profileSummary',
                    status: 'not_run',
                    result: 'surfaced_failure',
                    errorCode: 'provider_error',
                };
                const existingSummary = await this.getLayer('summary');
                if (existingSummary)
                    return existingSummary;
                throw err;
            }
            let parsed;
            try {
                const jsonMatch = response.text.match(/\{[\s\S]*\}/);
                if (!jsonMatch)
                    throw new Error('No JSON object in profile summary response');
                const raw = JSON.parse(jsonMatch[0]);
                parsed = validateSummaryLLMOutput(raw);
                routeLocalOutcome = {
                    operation: 'profileSummary',
                    status: 'success',
                    result: 'success',
                };
            }
            catch {
                // LLM returned malformed JSON — preserve existing summary rather than overwriting with empty data
                const existingSummary = await this.getLayer('summary');
                if (existingSummary) {
                    console.warn('[UserProfile] LLM JSON parse failed — preserving existing summary');
                    routeLocalOutcome = {
                        operation: 'profileSummary',
                        status: 'parse_failed',
                        result: 'fallback',
                        errorCode: 'parse_failed',
                    };
                    return existingSummary;
                }
                routeLocalOutcome = {
                    operation: 'profileSummary',
                    status: 'parse_failed',
                    result: 'fallback',
                    errorCode: 'parse_failed',
                };
                parsed = {
                    narrative: '',
                    directives: [],
                    ikigaiWeights: { passion: 0, mission: 0, profession: 0, vocation: 0 },
                };
            }
            const summary = {
                version: 1,
                generatedAt: Date.now(),
                stage: computeStage(behaviors?.missionPatterns.totalPresented ?? 0),
                narrative: parsed.narrative ?? '',
                topInterests: (interests?.clusters ?? [])
                    .flatMap(c => c.topics.map(t => ({
                    topic: t.name,
                    strength: t.strength,
                    trend: c.trend,
                    iabCategoryId: c.iabCategoryIds?.[0],
                })))
                    .sort((a, b) => b.strength - a.strength)
                    .slice(0, 10),
                activeContexts: (context?.activeIntents ?? [])
                    .filter(i => i.expiresAt > Date.now())
                    .slice(0, 5)
                    .map(i => ({
                    context: i.intent,
                    confidence: i.confidence,
                    detectedAt: i.detectedAt,
                    expiresAt: i.expiresAt,
                })),
                directives: parsed.directives ?? [],
                ikigaiWeights: parsed.ikigaiWeights ?? {
                    passion: 0,
                    mission: 0,
                    profession: 0,
                    vocation: 0,
                },
                confidence: computeConfidence(identity, interests, behaviors),
                dataPointCount: behaviors?.missionPatterns.totalPresented ?? 0,
                dataSourceCount: countDataSources(interests),
            };
            await this.putLayer('summary', summary);
            return summary;
        }
        finally {
            recordIkigaiRouteLocalOutcome(llm, routeLocalOutcome);
        }
    }
    createEmptySummary() {
        return {
            version: 1,
            generatedAt: 0,
            stage: 'cold_start',
            narrative: '',
            topInterests: [],
            activeContexts: [],
            directives: [],
            ikigaiWeights: { passion: 0, mission: 0, profession: 0, vocation: 0 },
            confidence: 0,
            dataPointCount: 0,
            dataSourceCount: 0,
        };
    }
}
// --- Helper functions ---
export function createEmptyBehaviors() {
    return {
        updatedAt: Date.now(),
        missionPatterns: {
            totalPresented: 0,
            totalAccepted: 0,
            totalDismissed: 0,
            totalCompleted: 0,
            totalRefinedViaChat: 0,
            categoryRates: {},
            acceptancePatterns: [],
            dismissalPatterns: [],
        },
        signalWeights: { explicitPositive: 1.0, explicitNegative: 1.0, chatRefinement: 1.0 },
        engagementRhythm: {
            avgSessionsPerWeek: 0,
            preferredTimes: [],
            avgMissionsPerSession: 0,
            responsiveness: 'batch',
        },
    };
}
function computeStage(dataPoints) {
    if (dataPoints === 0)
        return 'cold_start';
    if (dataPoints <= 20)
        return 'early';
    if (dataPoints <= 100)
        return 'developing';
    if (dataPoints <= 500)
        return 'rich';
    return 'mature';
}
function computeConfidence(identity, interests, behaviors) {
    let score = 0;
    if (identity?.ageRange)
        score += 0.1;
    if (identity?.gender)
        score += 0.1;
    if (identity?.economic)
        score += 0.1;
    if ((interests?.clusters.length ?? 0) > 0)
        score += 0.2;
    if ((interests?.clusters.length ?? 0) > 3)
        score += 0.1;
    if ((behaviors?.missionPatterns.totalPresented ?? 0) > 10)
        score += 0.2;
    if ((behaviors?.missionPatterns.totalPresented ?? 0) > 50)
        score += 0.2;
    return Math.min(1.0, score);
}
function countDataSources(interests) {
    if (!interests)
        return 0;
    // Count unique data origins: clusters with IAB IDs = IAB source, others = ikigai
    const hasIab = interests.clusters.some(c => c.iabCategoryIds.length > 0);
    const hasIkigai = interests.clusters.some(c => c.iabCategoryIds.length === 0);
    return (hasIab ? 1 : 0) + (hasIkigai ? 1 : 0) + (interests.brandAffinities.length > 0 ? 1 : 0);
}
/** Validate and sanitize LLM-generated summary JSON before storing */
function validateSummaryLLMOutput(raw) {
    const narrative = typeof raw.narrative === 'string' ? raw.narrative.slice(0, 2000) : '';
    const directives = [];
    if (Array.isArray(raw.directives)) {
        for (const d of raw.directives.slice(0, 10)) {
            if (d && typeof d.condition === 'string' && typeof d.preference === 'string') {
                directives.push({
                    condition: d.condition.slice(0, 200),
                    preference: d.preference.slice(0, 200),
                    confidence: typeof d.confidence === 'number' ? Math.max(0, Math.min(1, d.confidence)) : 0.5,
                    source: d.source === 'explicit' ? 'explicit' : 'inferred',
                });
            }
        }
    }
    const clampUnit = (v) => {
        const n = Number(v);
        return isNaN(n) ? 0 : Math.max(0, Math.min(1, n));
    };
    const w = raw.ikigaiWeights;
    const ikigaiWeights = w && typeof w === 'object'
        ? {
            passion: clampUnit(w.passion),
            mission: clampUnit(w.mission),
            profession: clampUnit(w.profession),
            vocation: clampUnit(w.vocation),
        }
        : { passion: 0, mission: 0, profession: 0, vocation: 0 };
    return { narrative, directives, ikigaiWeights };
}
//# sourceMappingURL=user-profile-store.js.map